import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/api.config';

interface ReportSummary {
  id: number;
  name: string;
  type: string;
  summary: {
    totalSales?: number;
    totalJobs?: number;
    totalAdSpend?: number;
    netProfit?: number;
    grossProfitPercent?: number;
  };
  metadata: {
    calculation_type?: string;
    month?: number;
    year?: number;
  };
  created_at: string;
  updated_at: string;
}

const ReportManager: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'sales' | 'adspend'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'sales'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl('/v2/reports/direct/list'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setReports(result.reports || []);
      } else if (response.status === 401) {
        setError('Authentication required');
        navigate('/login');
      } else {
        const result = await response.json();
        setError(result.message || 'Failed to load reports');
      }
    } catch (err) {
      setError(`Network error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reportId: number, reportName: string) => {
    if (!confirm(`Are you sure you want to delete "${reportName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(buildApiUrl(`/v2/reports/direct/${reportId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setReports(reports.filter(r => r.id !== reportId));
      } else {
        const result = await response.json();
        alert(result.message || 'Failed to delete report');
      }
    } catch (err) {
      alert(`Delete error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const filteredReports = reports.filter(report => {
    if (filter === 'all') return true;
    return report.type === filter;
  });

  const sortedReports = [...filteredReports].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortBy) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'sales':
        aValue = a.summary.totalSales || 0;
        bValue = b.summary.totalSales || 0;
        break;
      case 'date':
      default:
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
        break;
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const getMonthName = (month: number) => {
    return new Date(2024, month - 1).toLocaleString('default', { month: 'long' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchReports}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-gray-900">Generated Reports</h1>
            <button
              onClick={() => navigate('/admin/csv-generator')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              + Generate New Report
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Filter:</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as 'all' | 'sales' | 'adspend')}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Reports</option>
                <option value="sales">Sales Reports</option>
                <option value="adspend">Ad Spend Reports</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'sales')}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="date">Date Created</option>
                <option value="name">Name</option>
                <option value="sales">Total Sales</option>
              </select>
            </div>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </button>

            <div className="ml-auto text-sm text-gray-500">
              {filteredReports.length} of {reports.length} reports
            </div>
          </div>
        </div>

        {/* Reports Grid */}
        {sortedReports.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📊</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No reports found</h3>
            <p className="text-gray-500 mb-6">
              {filter === 'all' 
                ? 'Get started by generating your first report from a CSV file.'
                : `No ${filter} reports found. Try changing the filter or create a new report.`
              }
            </p>
            <button
              onClick={() => navigate('/admin/csv-generator')}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              Generate First Report
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedReports.map((report) => (
              <div key={report.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">{report.name}</h3>
                      <p className="text-sm text-gray-500">
                        {report.metadata?.month && report.metadata?.year && (
                          <>
                            {getMonthName(report.metadata.month)} {report.metadata.year}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        report.type === 'sales' 
                          ? 'bg-blue-100 text-blue-800' 
                          : report.type === 'combined'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {report.type.toUpperCase()}
                      </span>
                      {report.metadata?.calculation_type === 'cody' && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          CODY
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="space-y-3 mb-6">
                    {report.type === 'sales' && (
                      <>
                        {report.summary.totalSales && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Total Sales:</span>
                            <span className="text-sm font-medium text-green-600">
                              {formatCurrency(report.summary.totalSales)}
                            </span>
                          </div>
                        )}
                        {report.summary.totalJobs && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Jobs:</span>
                            <span className="text-sm font-medium">{report.summary.totalJobs}</span>
                          </div>
                        )}
                        {report.summary.netProfit !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Net Profit:</span>
                            <span className={`text-sm font-medium ${
                              report.summary.netProfit >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {formatCurrency(report.summary.netProfit)}
                            </span>
                          </div>
                        )}
                        {report.summary.grossProfitPercent !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Gross Margin:</span>
                            <span className="text-sm font-medium text-blue-600">
                              {formatPercentage(report.summary.grossProfitPercent)}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    
                    {report.type === 'adspend' && report.summary.totalAdSpend && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Total Ad Spend:</span>
                        <span className="text-sm font-medium text-red-600">
                          {formatCurrency(report.summary.totalAdSpend)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-400">
                      {new Date(report.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/reports/generated/${report.id}`)}
                        className="px-3 py-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(report.id, report.name)}
                        className="px-3 py-1 text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportManager;